import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

import Toast from '@ingka/toast';

interface ToastMessage {
    id: string;
    text: string | React.ReactElement;
    type: 'success' | 'error' | 'info' | 'warning';
    actionButtonText?: string;
    actionClick?: () => void;
    autoClose?: boolean;
    duration?: number;
}

interface ToastContextType {
    showToast: (message: ToastMessage) => void;
    success: (text: string | React.ReactElement, duration?: number) => void;
    error: (text: string | React.ReactElement, autoClose?: boolean) => void;
    info: (text: string | React.ReactElement, duration?: number) => void;
    warning: (text: string | React.ReactElement, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const calculateDuration = (text: string | React.ReactElement): number => {
    const textLength = typeof text === 'string'
        ? text.length
        : text.toString().length;
    const calculatedTime = textLength * 50;
    return Math.min(Math.max(calculatedTime, 5000), 10000);
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentMessage, setCurrentMessage] = useState<ToastMessage | null>(null);
    const [queue, setQueue] = useState<ToastMessage[]>([]);
    const timeoutRef = useRef<NodeJS.Timeout>();

    const processQueue = useCallback(() => {
        if (queue.length > 0 && !isOpen) {
            const nextMessage = queue[0];
            setQueue(prev => prev.slice(1));
            setCurrentMessage(nextMessage);
            setIsOpen(true);

            if (nextMessage.autoClose !== false) {
                const duration = nextMessage.duration || calculateDuration(nextMessage.text);
                timeoutRef.current = setTimeout(() => {
                    setIsOpen(false);
                }, duration);
            }
        }
    }, [queue, isOpen]);

    const handleTransitionEnd = useCallback(() => {
        if (!isOpen && currentMessage) {
            setCurrentMessage(null);
            setTimeout(processQueue, 100);
        }
    }, [isOpen, currentMessage, processQueue]);

    const handleCloseRequest = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setIsOpen(false);
    }, []);

    const showToast = useCallback((message: ToastMessage) => {
        const newMessage = {
            ...message,
            id: `${Date.now()}-${Math.random()}`,
        };

        if (!isOpen) {
            setCurrentMessage(newMessage);
            setIsOpen(true);

            if (newMessage.autoClose !== false) {
                const duration = newMessage.duration || calculateDuration(newMessage.text);
                timeoutRef.current = setTimeout(() => {
                    setIsOpen(false);
                }, duration);
            }
        } else {
            setQueue(prev => [...prev, newMessage]);
        }
    }, [isOpen]);

    const success = useCallback((text: string | React.ReactElement, duration?: number) => {
        showToast({
            id: '',
            text,
            type: 'success',
            autoClose: true,
            duration,
        });
    }, [showToast]);

    const error = useCallback((text: string | React.ReactElement, autoClose = false) => {
        showToast({
            id: '',
            text,
            type: 'error',
            autoClose,
        });
    }, [showToast]);

    const info = useCallback((text: string | React.ReactElement, duration?: number) => {
        showToast({
            id: '',
            text,
            type: 'info',
            autoClose: true,
            duration,
        });
    }, [showToast]);

    const warning = useCallback((text: string | React.ReactElement, duration?: number) => {
        showToast({
            id: '',
            text,
            type: 'warning',
            autoClose: true,
            duration,
        });
    }, [showToast]);

    return (
        <ToastContext.Provider value={{ showToast, success, error, info, warning }}>
            {children}
            {currentMessage && (
                currentMessage.actionButtonText ? (
                    <Toast
                        isOpen={isOpen}
                        text={currentMessage.text}
                        actionButtonText={currentMessage.actionButtonText}
                        actionClick={currentMessage.actionClick || (() => { })}
                        ariaLabel={`${currentMessage.type} notification`}
                        onCloseRequest={handleCloseRequest}
                        onTransitionEnd={handleTransitionEnd}
                        ariaLabelCloseBtn="Dismiss notification"
                    />
                ) : (
                    <Toast
                        isOpen={isOpen}
                        text={currentMessage.text}
                        onCloseRequest={handleCloseRequest}
                        onTransitionEnd={handleTransitionEnd}
                        ariaLabelCloseBtn="Dismiss notification"
                    />
                )
            )}
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export const ToastService = {
    _instance: null as ToastContextType | null,

    setInstance(instance: ToastContextType) {
        this._instance = instance;
    },

    success(message: string | React.ReactElement, duration?: number) {
        if (this._instance) {
            this._instance.success(message, duration);
        } else {
            console.warn('ToastService not initialized. Wrap your app with ToastProvider.');
        }
    },

    error(message: string | React.ReactElement, autoClose = false) {
        if (this._instance) {
            this._instance.error(message, autoClose);
        } else {
            console.warn('ToastService not initialized. Wrap your app with ToastProvider.');
        }
    },

    info(message: string | React.ReactElement, duration?: number) {
        if (this._instance) {
            this._instance.info(message, duration);
        } else {
            console.warn('ToastService not initialized. Wrap your app with ToastProvider.');
        }
    },

    warning(message: string | React.ReactElement, duration?: number) {
        if (this._instance) {
            this._instance.warning(message, duration);
        } else {
            console.warn('ToastService not initialized. Wrap your app with ToastProvider.');
        }
    },

    warningPassThrough(message: string | React.ReactElement, passThrough: any) {
        this.warning(message);
        return passThrough;
    },
};
